import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  ChatRule, 
  ChatRuleCreateDto, 
  ChatRuleUpdateDto,
  ChatRuleResponse,
  ChatRuleLatestResponse,
  ChatRuleLog,
  ChatRuleLogCreateDto,
  ChatRuleLogUpdateDto,
  ChatRuleLogResponse
} from "../../modules/shared/model";
import { 
  getLatestChatRule as getLatestChatRuleAction,
  getChatRules as getChatRulesAction, 
  getChatRuleById as getChatRuleByIdAction,
  createChatRuleAction,
  updateChatRuleAction,
  deleteChatRuleAction,
  createChatRuleLogAction,
  getChatRuleLogs as getChatRuleLogsAction,
  getChatRuleLogById as getChatRuleLogByIdAction,
  updateChatRuleLogAction,
  deleteChatRuleLogAction
} from "../Actions/ChatRulesActions";

// Async Thunks
export const fetchLatestChatRule = createAsyncThunk("chatRules/fetchLatest", async (_, { rejectWithValue }) => {
  try { 
    return await getLatestChatRuleAction(); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch latest chat rule"); 
  }
});

export const fetchChatRules = createAsyncThunk("chatRules/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getChatRulesAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch chat rules"); 
  }
});

export const fetchChatRuleById = createAsyncThunk("chatRules/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getChatRuleByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch chat rule"); 
  }
});

export const createChatRule = createAsyncThunk("chatRules/create", async (data: ChatRuleCreateDto, { rejectWithValue }) => {
  try { 
    return await createChatRuleAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create chat rule"); 
  }
});

export const updateChatRule = createAsyncThunk("chatRules/update", async ({ id, data }: { id: string; data: ChatRuleUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateChatRuleAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update chat rule"); 
  }
});

export const deleteChatRule = createAsyncThunk("chatRules/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteChatRuleAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete chat rule"); 
  }
});

// Chat Rule Logs Async Thunks
export const createChatRuleLog = createAsyncThunk("chatRules/createLog", async (data: ChatRuleLogCreateDto, { rejectWithValue }) => {
  try { 
    return await createChatRuleLogAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create chat rule log"); 
  }
});

export const fetchChatRuleLogs = createAsyncThunk("chatRules/fetchLogs", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getChatRuleLogsAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch chat rule logs"); 
  }
});

export const fetchChatRuleLogById = createAsyncThunk("chatRules/fetchLogById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getChatRuleLogByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch chat rule log"); 
  }
});

export const updateChatRuleLog = createAsyncThunk("chatRules/updateLog", async ({ id, data }: { id: string; data: ChatRuleLogUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateChatRuleLogAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update chat rule log"); 
  }
});

export const deleteChatRuleLog = createAsyncThunk("chatRules/deleteLog", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteChatRuleLogAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete chat rule log"); 
  }
});

// State interface
interface ChatRulesState {
  chatRules: ChatRule[];
  latestChatRule: ChatRuleLatestResponse | null;
  currentChatRule: ChatRule | null;
  chatRuleLogs: ChatRuleLog[];
  currentChatRuleLog: ChatRuleLog | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  logsPagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

// Initial state
const initialState: ChatRulesState = {
  chatRules: [],
  latestChatRule: null,
  currentChatRule: null,
  chatRuleLogs: [],
  currentChatRuleLog: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  logsPagination: {
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
};

// Slice
const chatRulesSlice = createSlice({
  name: "chatRules",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentChatRule: (state) => {
      state.currentChatRule = null;
    },
    clearCurrentChatRuleLog: (state) => {
      state.currentChatRuleLog = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
    setLogsPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.logsPagination.page = action.payload.page;
      state.logsPagination.limit = action.payload.limit;
    },
  },
  extraReducers: (builder) => {
    // Fetch latest chat rule
    builder
      .addCase(fetchLatestChatRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLatestChatRule.fulfilled, (state, action) => {
        state.loading = false;
        state.latestChatRule = action.payload;
      })
      .addCase(fetchLatestChatRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch all chat rules
    builder
      .addCase(fetchChatRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatRules.fulfilled, (state, action) => {
        state.loading = false;
        state.chatRules = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.totalPages,
          hasNextPage: action.payload.hasNextPage,
          hasPreviousPage: action.payload.hasPreviousPage,
        };
      })
      .addCase(fetchChatRules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch chat rule by ID
    builder
      .addCase(fetchChatRuleById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatRuleById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChatRule = action.payload;
      })
      .addCase(fetchChatRuleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create chat rule
    builder
      .addCase(createChatRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createChatRule.fulfilled, (state, action) => {
        state.loading = false;
        state.chatRules.unshift(action.payload);
        state.pagination.totalCount += 1;
      })
      .addCase(createChatRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update chat rule
    builder
      .addCase(updateChatRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChatRule.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.chatRules.findIndex(rule => rule.id === action.payload.id);
        if (index !== -1) {
          state.chatRules[index] = action.payload;
        }
        if (state.currentChatRule?.id === action.payload.id) {
          state.currentChatRule = action.payload;
        }
      })
      .addCase(updateChatRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete chat rule
    builder
      .addCase(deleteChatRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteChatRule.fulfilled, (state, action) => {
        state.loading = false;
        state.chatRules = state.chatRules.filter(rule => rule.id !== action.payload);
        state.pagination.totalCount -= 1;
        if (state.currentChatRule?.id === action.payload) {
          state.currentChatRule = null;
        }
      })
      .addCase(deleteChatRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create chat rule log
    builder
      .addCase(createChatRuleLog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createChatRuleLog.fulfilled, (state, action) => {
        state.loading = false;
        state.chatRuleLogs.unshift(action.payload);
        state.logsPagination.totalCount += 1;
      })
      .addCase(createChatRuleLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch chat rule logs
    builder
      .addCase(fetchChatRuleLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatRuleLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.chatRuleLogs = action.payload.data;
        state.logsPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.totalPages,
          hasNextPage: action.payload.hasNextPage,
          hasPreviousPage: action.payload.hasPreviousPage,
        };
      })
      .addCase(fetchChatRuleLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch chat rule log by ID
    builder
      .addCase(fetchChatRuleLogById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatRuleLogById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChatRuleLog = action.payload;
      })
      .addCase(fetchChatRuleLogById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update chat rule log
    builder
      .addCase(updateChatRuleLog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChatRuleLog.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.chatRuleLogs.findIndex(log => log.id === action.payload.id);
        if (index !== -1) {
          state.chatRuleLogs[index] = action.payload;
        }
        if (state.currentChatRuleLog?.id === action.payload.id) {
          state.currentChatRuleLog = action.payload;
        }
      })
      .addCase(updateChatRuleLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete chat rule log
    builder
      .addCase(deleteChatRuleLog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteChatRuleLog.fulfilled, (state, action) => {
        state.loading = false;
        state.chatRuleLogs = state.chatRuleLogs.filter(log => log.id !== action.payload);
        state.logsPagination.totalCount -= 1;
        if (state.currentChatRuleLog?.id === action.payload) {
          state.currentChatRuleLog = null;
        }
      })
      .addCase(deleteChatRuleLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  clearCurrentChatRule, 
  clearCurrentChatRuleLog,
  setPagination, 
  setLogsPagination 
} = chatRulesSlice.actions;
export default chatRulesSlice.reducer;